import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormElementTypeEnum,
  FormInterface,
} from 'src/shared/types/entities/form-interface'
import { getFormElementsSettingsIcons } from '../../../utils/get-form-elements-settings-icons'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import { FormAddNewElementSettingsItem } from './form-add-new-element-settings-item'
import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import { createFormCheckbox, createFormField } from '../../../create-form'
import {
  formAddNewElementSettingsList,
  HORIZONTAL_LINE_INDEXES,
} from './constants'
import { Toggle } from 'src/editor/shared/ui-lib/toggle'
import { FormElementNumberInput } from './form-element-number-input'

interface FormAddNewElementSettingsProps {
  entity: FormInterface
}

const HorizontalLine = () => <hr className="my-2 bg-divider00" />

export const FormAddNewElementSettings = ({
  entity,
}: FormAddNewElementSettingsProps) => {
  const { t } = useTranslation()

  const updateEntity = useUpdateEntity<FormInterface>()

  const getIsExisted = (slug: ContactSlugEnum) => {
    return entity.elements.some(
      element =>
        element.elementType === FormElementTypeEnum.Field &&
        element.slug === slug,
    )
  }

  const onToggle = (slug: ContactSlugEnum) => {
    const isExisted = getIsExisted(slug)
    if (isExisted) {
      updateEntity({
        ...entity,
        elements: entity.elements.filter(
          element =>
            element.elementType !== FormElementTypeEnum.Field ||
            element.slug !== slug,
        ),
      })
    } else {
      updateEntity({
        ...entity,
        elements: [...entity.elements, createFormField(slug)],
      })
    }
  }

  const checkboxes = entity.elements.filter(
    element => element.elementType === FormElementTypeEnum.Checkbox,
  )

  const onChangeNumberOfCheckboxes = (value: number) => {
    if (value > checkboxes.length) {
      updateEntity({
        ...entity,
        elements: [...entity.elements, createFormCheckbox()],
      })
    } else {
      const checkboxesToRemove = checkboxes[checkboxes.length - 1] // NOTE: Remove the last checkbox
      updateEntity({
        ...entity,
        elements: entity.elements.filter(
          element => element.id !== checkboxesToRemove.id,
        ),
      })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {formAddNewElementSettingsList.map((slug, index) => (
        <>
          {HORIZONTAL_LINE_INDEXES.includes(index) && <HorizontalLine />}
          {
            <FormAddNewElementSettingsItem
              key={slug}
              title={getContactSlugName(slug, t)}
              icon={getFormElementsSettingsIcons(slug)}
              controlComponent={() => (
                <Toggle
                  onChange={() => onToggle(slug)}
                  checked={getIsExisted(slug)}
                />
              )}
            />
          }
        </>
      ))}
      <HorizontalLine />
      <FormAddNewElementSettingsItem
        title={t('entity_settings.checkbox.header')}
        icon={CheckSquareIcon}
        controlComponent={() => (
          <FormElementNumberInput
            value={checkboxes.length}
            onChange={onChangeNumberOfCheckboxes}
          />
        )}
      />
    </div>
  )
}
